import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { handleResponseLanguage } from '../../../../../../../../../i18n';
import { TreeItem } from '../../../../../../../../design-system/src/content/components/TreeList';
import { useQuestionGroups } from '../../../../../../api/getQuestionGroupsByFlowId';
import {
  getTagContentFromArray,
  getTagContentFromStore,
} from '../../../../common/hooks/useTagContent';
import { AskLaterQuestion } from '../types';

interface TreeObject {
  [tag: string]: {
    [groupId: string]: {
      value: string;
      questions: {
        key: string;
        value: string;
      }[];
    };
  };
}

interface UseAskLaterTree {
  treeItems: TreeItem[];
  isLoading: boolean;
}

/**
 * This hook converts a list of AskLater questions to a tree structure that is useable
 * in the AskLaterQuestionsModal
 * @param askLaterQuestions The list of ask later questions
 * @returns A tree structure of the ask later questions to view it in a TreeList component
 */
export const useAskLaterTree = (
  flowId: string,
  askLaterQuestions?: AskLaterQuestion[],
): UseAskLaterTree => {
  const { t } = useTranslation();
  const { data: resp, isLoading } = useQuestionGroups({ flowId });

  const tree: TreeItem[] = useMemo(() => {
    if (askLaterQuestions?.length === 0 || !resp) return [];

    const askLaterQuestionsIds = askLaterQuestions?.map(
      ({ question_id }) => question_id,
    );
    const treeObject: TreeObject = {};

    /**
     * We build an object like this to make the tree build easier
     * {
     *   [tag]:{
     *     [groupId]: [questionObj, questionObj, ...]
     *     [groupId]: [questionObj, questionObj, ...]
     *     ...
     *   },
     *   [tag]:{
     *     [groupId]: [questionObj, questionObj, ...]
     *     ....
     *   }
     *   ...
     * }
     */
    resp.questionnaire.groups?.forEach(
      ({ id: groupId, questions, title: groupTitle }) =>
        questions.forEach(({ tag, id: questionId, title: questionTitle }) => {
          if (askLaterQuestionsIds?.includes(questionId)) {
            const tTag =
              getTagContentFromStore(tag) ||
              getTagContentFromArray(tag, resp.tag_content) ||
              tag;

            if (!treeObject[tTag]) {
              treeObject[tTag] = {};
            }
            treeObject[tTag][groupId] = {
              value: handleResponseLanguage(groupTitle),
              questions: [
                ...(treeObject[tTag]?.[groupId]?.questions || []),
                {
                  key: questionId,
                  value: handleResponseLanguage(questionTitle),
                },
              ],
            };
          }
        }),
    );

    // We can now construct the TreeItem array
    const treeItemArray = Object.entries(treeObject).map(([key, value]) => {
      return {
        key: key,
        value: t(key),
        children: Object.entries(value).map(([key, value]) => {
          return {
            key,
            value: value.value,
            children: value.questions,
          };
        }),
      };
    });

    return treeItemArray;
  }, [resp, askLaterQuestions]);

  return {
    treeItems: tree,
    isLoading,
  };
};

export default useAskLaterTree;
